import { SparklesIcon } from "./icons"
import { CoordinateInput } from "../../shared/components"
import { CursorGrid } from "../../shared/services"

import type { Point } from "../../shared/types"
import type { ReactNode } from "react"

const TOP_LEFT: Point = { x: 0, y: 0 }

const isSamePoint = (a: Point, b: Point) => a.x === b.x && a.y === b.y

interface Props {
  hotspot: Point
  autoHotspot: Point
  onChange: (hotspot: Point) => void
}

export const HotspotControls = ({ hotspot, autoHotspot, onChange }: Props) => {
  // Auto wins a tie with Top-left, so exactly one preset reads as pressed.
  const isAuto = isSamePoint(hotspot, autoHotspot)

  return (
    <div className="flex flex-col gap-2 rounded-box bg-base-300 p-3">
      <p className="flex items-baseline justify-between gap-3 text-sm font-bold">
        Hotspot
        <span className="text-xs font-semibold text-muted-content">{CursorGrid.SIZE} px grid</span>
      </p>
      <div role="group" aria-label="Hotspot presets" className="flex flex-wrap gap-1">
        <PresetButton pressed={isAuto} onClick={() => onChange(autoHotspot)}>
          <SparklesIcon className="size-3.5" />
          Auto
        </PresetButton>
        <PresetButton
          pressed={!isAuto && isSamePoint(hotspot, TOP_LEFT)}
          onClick={() => onChange(TOP_LEFT)}
        >
          Top-left
        </PresetButton>
        <PresetButton
          pressed={!isAuto && isSamePoint(hotspot, CursorGrid.CENTER)}
          onClick={() => onChange(CursorGrid.CENTER)}
        >
          Center
        </PresetButton>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <CoordinateInput axis="X" value={hotspot.x} onChange={(x) => onChange({ ...hotspot, x })} />
        <CoordinateInput axis="Y" value={hotspot.y} onChange={(y) => onChange({ ...hotspot, y })} />
      </div>
    </div>
  )
}

const PresetButton = ({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    aria-pressed={pressed}
    onClick={onClick}
    className={`btn gap-1.5 rounded-full font-bold btn-sm ${
      pressed ? "btn-soft btn-primary" : "border-base-content/10 bg-base-200 text-muted-content"
    }`}
  >
    {children}
  </button>
)
